const { path } = require('ramda')

function Currency(val, options = {}) {
  const { locale = 'nb', currency = 'NOK', decimals = 0 } = options
  if ([null, undefined].includes(val)) return
  return val.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
  })
}

const Clone = function () {
  return JSON.parse(JSON.stringify.apply(this, arguments))
}

function getDimensions(url) {
  return new Promise((resolve, reject) => {
    if (!url) return reject(new Error('Url is required'))
    let img = new Image()
    img.onload = function () {
      resolve({
        width: img.width,
        height: img.height,
      })
    }
    img.onerror = reject
    img.src = url
  })
}

function Limit(val, limit = 30) {
  if (val.length < limit) return val
  return `${val.substr(0, limit)}...`
}

function StripHtml(html) {
  let tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

function CheckConditions(item, shop) {
  if (!item || !item.conditions) return true

  let selected = getSelectedItems(shop, { ignoreConditions: true }).map(({ id }) => id)
  const ifCheck = !item.conditions.if.length || item.conditions.if.some(id => selected.includes(id))
  const ifnotCheck =
    !item.conditions.ifnot.length || item.conditions.ifnot.every(id => !selected.includes(id))

  return ifCheck && ifnotCheck
}

function ConditionFilter(value, shop) {
  // Category handler

  if (value.categories) {
    let children = value.categories.filter(subcat => CheckConditions(subcat, shop))
    if (!children.length) return
  }

  return CheckConditions(value, shop)
}

function getConditionalDependencies(id, data) {
  const getConditions = (v, k) => path(['conditions', k], v) || []
  const ValueOrDefault = (v, d) => v || d

  let ifs = new Set()
  let ifnots = new Set()

  ValueOrDefault(data.categories, []).forEach(category => {
    let cif = getConditions(category, 'if').includes(id)
    let cifn = getConditions(category, 'ifnot').includes(id)

    if (cif) ifs.add(category.id)
    if (cifn) ifnots.add(category.id)

    ValueOrDefault(category.categories, []).forEach(subcategory => {
      let scif = getConditions(subcategory, 'if').includes(id)
      let scifn = getConditions(subcategory, 'ifnot').includes(id)

      if (cif || scif) ifs.add(subcategory.id)
      if (cifn || scifn) ifnots.add(subcategory.id)

      ValueOrDefault(subcategory.items, []).forEach(item => {
        if (cif || scif || getConditions(item, 'if').includes(id)) ifs.add(item.id)
        if (cifn || scifn || getConditions(item, 'ifnot').includes(id)) ifnots.add(item.id)
      })
    })
  })

  return {
    if: Array.from(ifs),
    ifnot: Array.from(ifnots),
  }
}

function getReferencePropertyChanges(shop) {
  if (!shop) return []
  return Object.entries(
    getSelectedItems(shop)
      .map(({ referencePropertyChanges }) => referencePropertyChanges)
      .flat()
      .reduce((res, { key, delta }) => {
        if (!(key in res)) res[key] = 0
        res[key] += delta
        return res
      }, {}),
  ).map(([key, delta]) => ({ key, delta }))
}

function getSelectedItems(shop, options = {}) {
  const { ignoreConditions = false } = options

  const filter = value => ignoreConditions || CheckConditions(value, shop)

  const getItem = ({ items }) => items.filter(item => item.selected && filter(item))

  return shop.categories
    .filter(category => {
      return filter(category) && (!options.category || options.category === category.id)
    })
    .map(category => {
      if ('categories' in category) return (category.categories || []).filter(filter).map(getItem)
      return getItem(category)
    })
    .flat(Infinity)
}

function getContextualImages(shop = {}, item) {
  const contextualImages = shop.contextualImages || []
  const hasContextualImage = contextualImages && contextualImages.length
  if (!hasContextualImage) return []
  const selectedItems = getSelectedItems(shop).map(({ id }) => id)
  return (
    contextualImages.filter(({ conditions, appendTo }) => {
      return (
        Boolean(appendTo.find(id => id === item.id)) &&
        conditions.every(id => selectedItems.includes(id))
      )
    }) || {}
  ).flatMap(({ images = [] }) => images)
}

function ReduceItemsSum(total, item) {
  return total + getItemSum(item)
}

function getItemSum({ product, quantity }) {
  const multiplicator = (quantity.enabled && quantity.value) || 1
  return product.gross * multiplicator
}

function getCategoryTotal(id, shop, options = {}) {
  return getSelectedItems(shop, { category: id })
    .filter(({ isDefault }) => !isDefault)
    .reduce(ReduceItemsSum, 0)
}

function getShopTotal(shop) {
  return getSelectedItems(shop)
    .filter(({ isDefault }) => !isDefault)
    .reduce(ReduceItemsSum, 0)
}

function getItem(id, shop) {
  let result = {}

  shop.categories.find(category => {
    if (!category || !category.categories) return

    return category.categories.find(subcategory => {
      if (!subcategory || !subcategory.items) return

      let item = subcategory.items.find(item => item.id === id)
      if (!item) return

      result = { item, subcategory, category }
    })
  })

  return result
}

function getFloorplan(item, shop) {
  const { category } = getItem(item.id, shop)
  if (!category || !path(['floorplans', 'length'], category)) return

  return category.floorplans.filter(f => CheckConditions(f, shop)).find(Boolean)
}

function PromiseQueue() {
  let items = []
  let isRunning = false

  function run() {
    if (!items.length || isRunning) return
    let { action, onSuccess, onError } = items.shift()
    isRunning = true

    action()
      .then(onSuccess)
      .catch(onError)
      .finally(() => {
        isRunning = false
        return run()
      })
  }

  return function (action) {
    return new Promise((resolve, reject) => {
      items.push({ action, onSuccess: resolve, onError: reject })
      run()
    })
  }
}

function toArray(str) {
  if (str instanceof Array) return str
  if (![null, undefined].includes(str)) return [str]
  return []
}

function ResetShop(shop) {
  shop.categories.forEach(category => {
    category.status = 'unsigned'

    category.categories.forEach(subcategory => {
      subcategory.items.forEach(item => {
        item.selected = item.isDefault || false
        if (path(['product', 'variants', 'items', 'length'], item)) {
          const defaultVariant = getDefaultVariant(item)
          item.variant = {
            category: item.product.variants.category,
            value: defaultVariant.internalId,
          }
        }

        if (item.quantity && item.quantity.enabled) item.quantity.value = item.quantity.min
      })
    })
  })
  return shop
}

function Storage(storage) {
  const store = window[storage || 'sessionStorage']

  if (!store) throw new Error('Storage not found')

  return {
    get(key) {
      if (!key) return

      try {
        return JSON.parse(store.getItem(key))
      } catch (err) {
        return store.getItem(key)
      }
    },
    set(key, value) {
      if (!key) return
      if ([null, undefined].includes(value)) return store.removeItem(key)

      return store.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value)
    },
    has(key) {
      return store.getItem(key) !== null
    },
    remove(key) {
      if (!key || !this.has(key)) return
      return store.removeItem(key)
    },
  }
}

const getDefaultVariant = item => {
  if (!path(['product', 'variants', 'items'], item)) return
  return item.product.variants.items.find(({ isDefault }) => isDefault)
}

const convertSelectionToBase64 = (shop = {}) => {
  const selections = getSelectedItems(shop)
    .filter(item => {
      const defaultVariant = getDefaultVariant(item)
      const hasVariantChange =
        defaultVariant && path(['variant', 'value'], item) !== defaultVariant.internalId

      return hasVariantChange || !item.isDefault
    })
    .map(i => {
      return {
        id: i.id,
        ...(i.quantity.enabled && { quantity: i.quantity.value }),
        ...(i.variant && i.variant.value && { variant: i.variant.value }),
      }
    })

  if (!selections.length) return

  return Base64Util.to(selections)
}

const Base64Util = {
  to: data => {
    try {
      if (typeof data === 'object') data = JSON.stringify(data)
      return btoa(data)
    } catch (error) {
      return
    }
  },
  from: data => {
    try {
      return JSON.parse(atob(data))
    } catch (error) {
      return
    }
  },
}

function isNumeric(str) {
  if (typeof str === 'number') return true
  if (typeof str != 'string') return false
  return !isNaN(str) && !isNaN(parseFloat(str))
}

export {
  Clone,
  Currency,
  Limit,
  StripHtml,
  CheckConditions,
  getConditionalDependencies,
  ConditionFilter,
  getDimensions,
  getShopTotal,
  getSelectedItems,
  getCategoryTotal,
  getItem,
  getItemSum,
  getFloorplan,
  PromiseQueue,
  toArray,
  Storage,
  Base64Util,
  convertSelectionToBase64,
  ResetShop,
  getReferencePropertyChanges,
  getContextualImages,
  isNumeric,
}
